import * as yup from "yup";
import { noWhitespaceValidation } from "../../utils/noWhitespaceValidation/noWhitespaceValidation";
import { NON_ZERO_REG_EXP, ONLY_LETTERS_AND_SPACE_REG_EXP, ONLY_NUMBERS_REG_EXP, } from "../../constants/FormRegex";
import { ContactPreferences } from "../../types/ContactDetailsValues";
import { EMAIL_REG_EXP } from "../../constants/EmailRegex";
import { ALTERNATE_PHONE_REG_EXP, MOBILE_REG_EXP, } from "../../constants/ContactNumbersRegex";
import { AssignedRoles } from "../../types/ContactDetailRoles";
import {
  hasAnyRoleAssigned,
  hasOnlyTargetRoleAssigned,
} from "../ContactDetailsForm/ContactDetailsAndRole/_/utils/AssignedRolesUtils";

yup.addMethod<yup.StringSchema>(
  yup.string,
  "noWhitespace",
  noWhitespaceValidation,
);

export const firstNameSchema = yup
  .string()
  .noWhitespace("First name is invalid")
  .max(50, "Maximum length is 50")
  .required("First name is required");

export const lastNameSchema = yup
  .string()
  .noWhitespace("Last name is invalid")
  .max(50, "Maximum length is 50")
  .required("Last name is required");

export const businessNameSchema = yup
  .string()
  .noWhitespace("Business name is invalid")
  .max(100, "Maximum length is 100 characters");

export const contactPreferenceSchema = yup.object().shape({
  email: yup.boolean(),
  sms: yup.boolean(),
});

export const eitherMobileOrAlternatePhoneSchema = yup
  .string()
  .when("workContactPreferences", {
    is: (workContactPreferences: ContactPreferences) =>
      workContactPreferences.sms,
    then: yup
      .string()
      .noWhitespace("Enter a valid phone number")
      .matches(MOBILE_REG_EXP, "Enter a valid phone number")
      .required("Mobile is required"),
    otherwise: yup
      .string()
      .noWhitespace("Enter a valid phone number")
      .matches(MOBILE_REG_EXP, "Enter a valid mobile number"),
  })
  .when("alternatePhone", {
    is: (alternatePhone: string) => !alternatePhone || alternatePhone === "",
    then: yup
      .string()
      .noWhitespace("Enter a valid phone number")
      .matches(MOBILE_REG_EXP, "Enter a valid phone number")
      .required("Mobile is required"),
    otherwise: yup
      .string()
      .noWhitespace("Enter a valid phone number")
      .matches(MOBILE_REG_EXP, "Enter a valid mobile number"),
  });

export const optionalMobilePhoneSchema = yup
  .string()
  .when("workContactPreferences", {
    is: (workContactPreferences: ContactPreferences) =>
      workContactPreferences.sms,
    then: yup
      .string()
      .noWhitespace("Enter a valid phone number")
      .matches(MOBILE_REG_EXP, "Enter a valid phone number")
      .required("Mobile is required"),
    otherwise: yup
      .string()
      .nullable()
      .noWhitespace("Enter a valid phone number")
      .matches(MOBILE_REG_EXP, "Enter a valid mobile number"),
  });

export const alternatePhoneSchema = yup
  .string()
  .noWhitespace("Enter a valid alternate phone number")
  .nullable()
  .matches(ALTERNATE_PHONE_REG_EXP, "Enter a valid alternate phone number");

export const requiredEmailSchema = yup
  .string()
  .noWhitespace("Enter a valid email")
  .matches(EMAIL_REG_EXP, "Enter a valid email")
  .max(50, "Maximum length is 50")
  .required("Email is required");

export const optionalEmailSchema = yup.string().when("workContactPreferences", {
  is: (workContactPreferences: ContactPreferences) =>
    workContactPreferences.email,
  then: yup
    .string()
    .noWhitespace("Enter a valid email")
    .matches(EMAIL_REG_EXP, "Enter a valid email")
    .max(50, "Maximum length is 50")
    .required("Email is required"),
  otherwise: yup
    .string()
    .noWhitespace("Enter a valid email")
    .matches(EMAIL_REG_EXP, "Enter a valid email")
    .max(50, "Maximum length is 50")
    .nullable(),
});

export const billingAddressDisplaySchema = yup
  .string()
  .when("manuallyEnteredBillingAddress", {
    is: false,
    then: yup.string().test(
      "is-valid-address",
      "Billing address is invalid",

      function (value) {
        const { from } = this as yup.TestContext & {
          /* eslint-disable  @typescript-eslint/no-explicit-any */
          from: any;
        };

        if (!value) {
          return false;
        }

        const contactDetailsValues = from[0];
        const formValues = from[1];

        if (!contactDetailsValues || !formValues) {
          return false;
        }

        const isUserLoggedIn = formValues.value.isUserLoggedIn as boolean;
        const billingAddressId = contactDetailsValues.value
          .billingAddressId as string;
        const roles = contactDetailsValues.value.roles as AssignedRoles;

        if (
          isUserLoggedIn
          && roles.requester
          && roles.quotePayer
        ) {
          return true;
        }

        return billingAddressId !== undefined && billingAddressId !== "";
      },
    ),
    otherwise: yup.string(),
  });

export const manuallyEnteredBillingAddressSchema = yup
  .object()
  .when("manuallyEnteredBillingAddress", {
    is: true,
    then: yup.object({
      addressString: yup
        .string()
        .noWhitespace("Street address / PO Box or Private Bag is invalid")
        .required("Street address / PO Box or Private Bag is required")
        .max(100, "Maximum length is 100")
        .min(5, "Street address / PO Box or Private Bag is invalid"),
      suburb: yup
        .string()
        .noWhitespace("Suburb is invalid")
        .max(50, "Maximum length is 50")
        .matches(
          ONLY_LETTERS_AND_SPACE_REG_EXP,
          "Suburb can only contain letters",
        ),
      city: yup
        .string()
        .noWhitespace("City is invalid")
        .required("City is required")
        .max(50, "Maximum length is 50")
        .matches(
          ONLY_LETTERS_AND_SPACE_REG_EXP,
          "City can only contain letters",
        ),
      postcode: yup
        .string()
        .noWhitespace("Postcode is invalid")
        .required("Postcode is required")
        .matches(ONLY_NUMBERS_REG_EXP, "Postcode can only contain numbers")
        .matches(NON_ZERO_REG_EXP, "Invalid postcode")
        .length(4, "Postcode should contain 4 numbers, please update"),
    }),
  });

export const nullOrEmptyStringSchema = yup.string().notRequired().nullable();

export const contactDetailsSchema = yup.array().of(
  yup.object().shape({
    role: yup.string().required(),
    sameAsRole: yup.string().required(),
    roles: yup.object().shape({
      requester: yup.boolean(),
      quotePayer: yup.boolean(),
      propertyOwner: yup.boolean(),
      siteAccessContact: yup.boolean(),
    }),
    firstName: yup.string().when("roles", {
      is: (roles: AssignedRoles) => hasAnyRoleAssigned(roles),
      then: firstNameSchema,
      otherwise: nullOrEmptyStringSchema,
    }),
    lastName: yup.string().when("roles", {
      is: (roles: AssignedRoles) => hasAnyRoleAssigned(roles),
      then: lastNameSchema,
      otherwise: nullOrEmptyStringSchema,
    }),
    email: yup
      .string()
      .when("roles", {
        is: (roles: AssignedRoles) =>
          hasOnlyTargetRoleAssigned("Site contact", roles),
        then: optionalEmailSchema,
      })
      .when("roles", {
        is: (roles: AssignedRoles) =>
          hasAnyRoleAssigned(roles) &&
          !hasOnlyTargetRoleAssigned("Site contact", roles),
        then: requiredEmailSchema,
      })
      .when("roles", {
        is: (roles: AssignedRoles) => !hasAnyRoleAssigned(roles),
        then: nullOrEmptyStringSchema,
      }),
    mobile: yup
      .string()
      .when("roles", {
        is: (roles: AssignedRoles) =>
          hasOnlyTargetRoleAssigned("Property owner", roles),
        then: optionalMobilePhoneSchema,
      })
      .when("roles", {
        is: (roles: AssignedRoles) =>
          hasAnyRoleAssigned(roles) &&
          !hasOnlyTargetRoleAssigned("Property owner", roles),
        then: eitherMobileOrAlternatePhoneSchema,
      })
      .when("roles", {
        is: (roles: AssignedRoles) => !hasAnyRoleAssigned(roles),
        then: nullOrEmptyStringSchema,
      }),
    alternatePhone: yup.string().when("roles", {
      is: (roles: AssignedRoles) => hasAnyRoleAssigned(roles),
      then: alternatePhoneSchema,
      otherwise: nullOrEmptyStringSchema,
    }),
    businessName: yup.string().when("roles", {
      is: (roles: AssignedRoles) => hasAnyRoleAssigned(roles),
      then: businessNameSchema,
      otherwise: nullOrEmptyStringSchema,
    }),
    workContactPreferences: yup.object().when("roles", {
      is: (roles: AssignedRoles) => hasAnyRoleAssigned(roles),
      then: contactPreferenceSchema,
      otherwise: yup.object(),
    }),
    quoteContactPreferences: yup.object().when("roles", {
      is: (roles: AssignedRoles) => hasAnyRoleAssigned(roles),
      then: contactPreferenceSchema,
      otherwise: yup.object(),
    }),
    billingAddressId: yup.string().nullable(),
    billingAddressDisplay: yup.string().when("roles", {
      is: (roles: AssignedRoles) =>
        hasAnyRoleAssigned(roles) && roles.quotePayer,
      then: billingAddressDisplaySchema,
      otherwise: nullOrEmptyStringSchema,
    }),
    manuallyEnteredBillingAddress: yup.boolean(),
    manualBillingAddress: yup.object().when("roles", {
      is: (roles: AssignedRoles) =>
        hasAnyRoleAssigned(roles) && roles.quotePayer,
      then: manuallyEnteredBillingAddressSchema,
      otherwise: yup.object(),
    }),
  }),
);

export const DisconnectionOrderDetailsFormSchema = yup.object().shape({
  isUserLoggedIn: yup.boolean(),
  propertyDetails: yup.object().shape({
    icpAddressLookupEnabled: yup.boolean(),

    icpAddressLookup: yup.object({
      id: yup
        .string()
        .test(
          "icp-validity",
          "Invalid ICP number provided, please update",
          function () {
            const { id } = this.parent;
            return id === undefined || id.length === 0 || id.length === 15;
          },
        ),
      label: yup.string(),
    }),

    meterInfo: yup.object().when("icpAddressLookupEnabled", {
      // Address provided by ICP
      is: true,
      then: yup.object({
        meterCount: yup.number(),
        meters: yup.array().of(
          yup.object().shape({
            location: yup
              .string()
              .required("Please provide the location of the meter")
              .max(100, "Maximum length is 100"),
            serialNumber: yup.string().max(20, "Maximum length is 20"),
          }),
        ),
        addressInformation: yup.object({
          streetAddress: yup.string(),
          streetAddressId: yup.string(),
          manuallyEnteredAddress: yup.boolean(),
          manualStreetAddress: yup.object(),
          additionalAddressInformation: yup.string(),
        }),
      }),
      // Meter serial and address need to be added
      otherwise: yup.object({
        meterCount: yup
          .number()
          .moreThan(0, "Please provide meter serial number as no ICP provided"),
        meters: yup.array().of(
          yup.object().shape({
            location: yup
              .string()
              .required("Please provide the location of the meter")
              .max(100, "Maximum length is 100"),
            serialNumber: yup
              .string()
              .required("Please provide the meter serial number")
              .max(20, "Maximum length is 20"),
          }),
        ),
        addressInformation: yup.object({
          manuallyEnteredAddress: yup.boolean(),
          streetAddress: yup.string().when("manuallyEnteredAddress", {
            is: false,

            then: yup.string().test(
              "is-valid-address",
              "Please provide the address",

              function (value) {
                const { from } = this as yup.TestContext & {
                  /* eslint-disable  @typescript-eslint/no-explicit-any */
                  from: any;
                };

                if (!value) {
                  return false;
                }

                const formValues = from[0];
                if (formValues) {
                  const streetAddressId = from[0].value
                    .streetAddressId as string;
                  return (
                    streetAddressId !== undefined && streetAddressId !== ""
                  );
                }

                return false;
              },
            ),
            otherwise: yup.string(),
          }),
          manualStreetAddress: yup.object().when("manuallyEnteredAddress", {
            is: true,
            then: yup.object({
              streetNumber: yup
                .string()
                .noWhitespace("Street Number is invalid")
                .required("Street Number is required")
                .max(6, "Maximum length is 6"),
              streetName: yup
                .string()
                .noWhitespace("Street Name is invalid")
                .required("Street Name is required")
                .max(50, "Maximum length is 50"),
              suburb: yup
                .string()
                .noWhitespace("Suburb is invalid")
                .required("Suburb is required")
                .max(50, "Maximum length is 50")
                .matches(ONLY_LETTERS_AND_SPACE_REG_EXP, "Suburb can only contain letters"),
              unit: yup
                .string()
                .noWhitespace("Unit is invalid")
                .max(5, "Maximum length is 5"),
              postcode: yup
                .string()
                .noWhitespace("Postcode is invalid")
                .required("Postcode is required")
                .matches(ONLY_NUMBERS_REG_EXP, "Postcode can only contain numbers")
                .matches(NON_ZERO_REG_EXP, "Invalid postcode")
                .length(4, "Postcode should contain 4 numbers, please update"),
            }),
          }),
          additionalAddressInformation: yup
            .string()
            .noWhitespace("Additional address information is invalid")
            .max(100, "Additional address information has a 100 characters limit"),
        }),
      }),
    }),

    type: yup.string().required("Please select property type"),
    guardDogs: yup.boolean(),
    lockedGate: yup.boolean(),
  }),
  disconnectionDetails: yup.object().shape({
    removalReason: yup.string().required("Please provide a reason"),
    hasTenants: yup.string().required(),
    hazards: yup.string().max(50, "Maximum length is 50"),
    otherInformation: yup.string().max(2000, "Maximum length is 50"),
    filesUploadStatus: yup.string(),
    meterPhotos: yup.array().when("filesUploadStatus", {
      is: (filesUploadStatus: string) => !filesUploadStatus,
      then: yup
        .array()
        .of(
          yup.object().shape({
            fileName: yup.string().required("File is required"),
            md5: yup.string().required("File md5 is required"),
          }),
        )
        .max(5, "Max of 5 images allowed"),
    }),
  }),
  contactDetails: contactDetailsSchema,
});
